
import React, { useState, useEffect, useMemo } from 'react'
import { Login, PasswordReset, Auth, Config } from '@cleeng/mediastore-sdk';
import { useParams } from 'react-router-dom';
import Header from '../../components/Checkout/Header';
import CleengFooter from '../../components/Footer/CleengFooter.js';
import checkout_svc from '../../services/checkout';
import docket from '../../docket-no-svc';
import nmx_svc from '../../newsmax-svc.js'
import { take } from 'rxjs/operators';
import ShippingDetailsComponent from '../../components/Checkout/ShippingDetail';
import { EMPTY_SHIPPING_DETAILS, validateShippingDetails } from '../../components/Checkout/shipping-validation';
import { getPromotion } from '../../components/Promotions';

const LOGIN_FORM_NAME = "login";
const PWD_RESET_FORM_NAME = "pwdreset";

const UpdateShippingAddressPage = () => {

    const location = window.location.href || null;
    const referrer = window.document.referrer || null;

    const { promoCode } = useParams();
    const promotion = useMemo(() => getPromotion(promoCode), [promoCode]);

    const [authForm, setAuthForm] = useState(LOGIN_FORM_NAME);
    const isLoggedIn = Auth.isLogged();

    const [eligibility, setEligibility] = useState({ checking: true, eligible: false, outOfTrialPeriod: false });
    const [shippingDetails, setShippingDetails] = useState(EMPTY_SHIPPING_DETAILS);
    const [saveState, setSaveState] = useState({ saving: false, saved: false, error: false });

    const shippingErrors = validateShippingDetails(shippingDetails, { requirePhone: promotion.shipping.requirePhone });
    const isShippingValid = Object.keys(shippingErrors).length === 0;

    useEffect(() => {
        if (!isLoggedIn) {
            setEligibility({ checking: false, eligible: false, outOfTrialPeriod: false });
            return;
        }

        setEligibility({ checking: true, eligible: false, outOfTrialPeriod: false });

        const sub = nmx_svc.get_subscription()
            .pipe(take(1))
            .subscribe({
                next: (subscription) => {
                    const items = (subscription && subscription.responseData && subscription.responseData.items) || [];
                    const eligible = items.some((item) => item.offerId === promotion.offerId);
                    const outOfTrialPeriod = items.some((item) => item.offerId === promotion.offerId && item.inTrial);
                    setEligibility({ checking: false, eligible, outOfTrialPeriod });
                },
                error: (ex) => {
                    console.error("Get subscription failed", ex);
                    setEligibility({ checking: false, eligible: false });
                }
            });

        return () => sub.unsubscribe();
    }, [isLoggedIn, promotion.offerId]);

    function handleSave() {
        setSaveState({ saving: true, saved: false, error: false });

        const payload = {
            docketNo: docket.docket_no,
            promoCode,
            offerId: promotion.offerId,
            shippingAddress: shippingDetails,
            location,
            referrer,
            userAgent: window.navigator.userAgent,
        };

        checkout_svc.save_shipping_address(payload)
            .then(() => setSaveState({ saving: false, saved: true, error: false }))
            .catch(() => setSaveState({ saving: false, saved: false, error: true }));
    }

    function renderAuth() {
        if (authForm === PWD_RESET_FORM_NAME) {
            return <PasswordReset onSuccess={() => setAuthForm(LOGIN_FORM_NAME)} />;
        }
        return (
            <Login
                onSuccess={() => setAuthForm("")}
                onPasswordResetClick={() => setAuthForm(PWD_RESET_FORM_NAME)}
            />
        );
    }

    function renderContent() {
        if (!promotion.shipping.required) {
            return (
                <div className="text-center mt-5 mb-5">
                    <h5 className="text-red">There is nothing to ship for this offer.</h5>
                    <p className="text-gray">This offer does not include a shipped item, so there is no address to update.</p>
                </div>
            );
        }

        if (!isLoggedIn) {
            return renderAuth();
        }

        if (eligibility.checking) {
            return <p className="text-center mt-5 mb-5">Checking your account...</p>;
        }

        if (eligibility.outOfTrialPeriod) {
            return (
                <div className="text-center mt-5 mb-5">
                    <h5 className="text-red">Your trial period has ended.</h5>
                    <p className="text-gray">You can no longer update the shipping address for this offer.</p>
                </div>
            );
        }

        if (!eligibility.eligible) {
            return (
                <div className="text-center mt-5 mb-5">
                    <h5 className="text-red">We couldn't find this book offer on your account.</h5>
                    <p className="text-gray">Only subscribers of this offer can update its shipping address.</p>
                </div>
            );
        }

        if (saveState.saved) {
            return (
                <div className="text-center mt-5 mb-5">
                    <h5>Your shipping address has been updated.</h5>
                </div>
            );
        }

        return (
            <>
                <div className="head-line text-center">
                    <h3>Update the shipping address for your book offer.</h3>
                </div>
                <div className="row">
                    <div className="col-md-6 offset-md-3">
                        <ShippingDetailsComponent
                            shippingDetails={shippingDetails}
                            setShippingDetails={setShippingDetails}
                            errors={shippingErrors}
                            includePhone={promotion.shipping.includePhone}
                            requirePhone={promotion.shipping.requirePhone}
                        />
                        <div className="text-center mt-3 mb-5">
                            <button
                                type="button"
                                disabled={!isShippingValid || saveState.saving}
                                onClick={handleSave}
                            >
                                {saveState.saving ? "Saving..." : "Save Address"}
                            </button>
                            {saveState.error &&
                                <p className="text-red mt-2">Something went wrong while saving your address. Please try again.</p>
                            }
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            {isLoggedIn && <Header isHomePage onGiftToggle={() => { }} isGiftMode={false} />}
            <div className="container">
                {renderContent()}
                <CleengFooter />
            </div>
        </>
    );
};

export default UpdateShippingAddressPage;
